'use client';
import React, { useState } from 'react';
import { Search, Tag, Zap, Gift } from 'lucide-react';
import ChainCard from './ChainCard';
import { chains, paymentMethods } from './directoryData';

export default function DirectoryContent() {
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState<string>('all');
  const [dealFilter, setDealFilter] = useState<string>('all');

  const filtered = chains?.filter((chain) => {
    const matchSearch =
      chain?.name?.toLowerCase()?.includes(search?.toLowerCase()) ||
      chain?.description?.toLowerCase()?.includes(search?.toLowerCase());
    const matchPayment =
      activeFilter === 'all' || chain?.paymentMethods?.includes(activeFilter);
    const matchDeal =
      dealFilter === 'all' ||
      (dealFilter === 'active' && chain?.dealStatus === 'active') ||
      (dealFilter === 'gift' && chain?.type === 'gift-card');
    return matchSearch && matchPayment && matchDeal;
  });

  return (
    <div className="flex flex-col gap-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white tracking-tight">Fast Food Crypto Directory</h1>
          <p className="text-sm text-zinc-400 mt-1">
            {chains?.length} Bitcoin-friendly chains &amp; platforms · Updated April 2026
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="badge-active">
            <Zap size={10} />
            {chains?.filter((c) => c?.dealStatus === 'active')?.length} Active Deals
          </span>
          <span className="badge-gold">
            <Gift size={10} />
            {chains?.filter((c) => c?.type === 'gift-card')?.length} Gift Card Platforms
          </span>
        </div>
      </div>
      {/* Search + Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input
            type="text"
            placeholder="Search chains, platforms, or payment methods..."
            value={search}
            onChange={(e) => setSearch(e?.target?.value)}
            className="input-base pl-9"
          />
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* Deal filter */}
          <div className="flex items-center gap-1 bg-zinc-900 border border-zinc-800 rounded-lg p-1">
            {[
              { key: 'all', label: 'All' },
              { key: 'active', label: 'Active Deals' },
              { key: 'gift', label: 'Gift Cards' },
            ]?.map((f) => (
              <button
                key={`deal-filter-${f?.key}`}
                onClick={() => setDealFilter(f?.key)}
                className={`px-3 py-1.5 rounded text-xs font-medium transition-all duration-150 ${
                  dealFilter === f?.key
                    ? 'bg-btcgold-500 text-zinc-950' :'text-zinc-400 hover:text-white'
                }`}
              >
                {f?.label}
              </button>
            ))}
          </div>
        </div>
      </div>
      {/* Payment method filter chips */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs text-zinc-500 font-medium">Payment method:</span>
        {paymentMethods?.map((method) => (
          <button
            key={`method-chip-${method?.key}`}
            onClick={() => setActiveFilter(method?.key)}
            className={`px-3 py-1 rounded-full text-xs font-medium border transition-all duration-150 ${
              activeFilter === method?.key
                ? 'bg-btcgold-500/15 text-btcgold-400 border-btcgold-500/40' :'bg-zinc-900 text-zinc-400 border-zinc-800 hover:border-zinc-600 hover:text-zinc-200'
            }`}
          >
            {method?.label}
          </button>
        ))}
      </div>
      {/* Results count */}
      <div className="flex items-center justify-between">
        <span className="text-xs text-zinc-500">
          Showing {filtered?.length} of {chains?.length} entries
        </span>
        {(search || activeFilter !== 'all' || dealFilter !== 'all') && (
          <button
            onClick={() => { setSearch(''); setActiveFilter('all'); setDealFilter('all'); }}
            className="text-xs text-btcgold-400 hover:text-btcgold-300 transition-colors"
          >
            Clear filters
          </button>
        )}
      </div>
      {/* Chain cards grid */}
      {filtered?.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-3 gap-4">
          {filtered?.map((chain) => (
            <ChainCard key={chain?.id} chain={chain} />
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-20 gap-4">
          <div className="w-12 h-12 rounded-xl bg-zinc-800 flex items-center justify-center">
            <Search size={24} className="text-zinc-500" />
          </div>
          <div className="text-center">
            <p className="text-white font-semibold">No chains match your filters</p>
            <p className="text-sm text-zinc-500 mt-1">
              Try adjusting your search or clearing the payment method filter.
            </p>
          </div>
          <button
            onClick={() => { setSearch(''); setActiveFilter('all'); setDealFilter('all'); }}
            className="btn-ghost text-sm"
          >
            Clear all filters
          </button>
        </div>
      )}
      {/* Info banner */}
      <div className="rounded-xl border border-zinc-800 bg-zinc-900/50 p-5">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg bg-btcgold-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
            <Tag size={16} className="text-btcgold-400" />
          </div>
          <div>
            <p className="text-sm font-semibold text-white">How crypto food payments work</p>
            <p className="text-sm text-zinc-400 mt-1 leading-relaxed">
              Most chains accept crypto via payment apps like{' '}
              <span className="text-btcgold-400 font-medium">Flexa</span>,{' '}
              <span className="text-btcgold-400 font-medium">Fold</span>, or{' '}
              <span className="text-btcgold-400 font-medium">Bakkt</span> which convert crypto to fiat
              at point of sale. Gift card platforms let you convert Bitcoin to brand gift cards usable
              at hundreds of chains. Always verify current availability at your local location.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}